import type {LoggerDomain, LogLevel} from "@cm/log"
import {loggerFactory} from "#browser-log/factory"

/**
 * Wire format for log-level synchronization between main thread and workers.
 *
 * Web Workers have no access to localStorage, so the main thread broadcasts the full
 * configuration (initial snapshot + every subsequent change) and the worker mirrors it
 * into its own in-memory `LoggerConfig`.
 */
export interface CmLogConfigMessage {
    __cmLogConfig: true
    global: LogLevel
    domains: Partial<Record<LoggerDomain, LogLevel>>
}

/**
 * Minimal subset of the `Worker` interface needed to push config updates.
 * Accepts both real `Worker` instances and the wrapper used by the platform.
 */
export interface LogConfigWorkerSink {
    postMessage(message: unknown): void
}

const MESSAGE_KEY = "__cmLogConfig"

export function isLogConfigMessage(data: unknown): data is CmLogConfigMessage {
    return typeof data === "object" && data !== null && (data as Record<string, unknown>)[MESSAGE_KEY] === true
}

/**
 * Send the current logging configuration to a worker, then keep it in sync by forwarding
 * every subsequent change. Returns an unsubscribe function that should be called when the
 * worker is terminated.
 *
 * Call this from the main thread, immediately after spawning the worker.
 */
export function registerLoggerWorker(worker: LogConfigWorkerSink): () => void {
    const send = () => {
        const snapshot = loggerFactory.getSnapshot()
        const message: CmLogConfigMessage = {
            __cmLogConfig: true,
            global: snapshot.global,
            domains: snapshot.domains,
        }
        try {
            worker.postMessage(message)
        } catch {
            // Worker may have been terminated between subscribe and notify.
        }
    }
    send()
    return loggerFactory.subscribe(send)
}

/**
 * Apply a config message inside a worker. Use together with `isLogConfigMessage` to filter
 * incoming messages before they reach the worker's normal dispatch.
 */
export function applyLogConfigMessage(message: CmLogConfigMessage): void {
    loggerFactory.applySnapshot({global: message.global, domains: message.domains})
}
